import { IAnyModelType, types, SnapshotIn } from 'mobx-state-tree';
import { DateTime } from 'luxon';

// include any new properties which require to be a DateTimeType
const DATETIME_PROPERTY_WHITELIST = {
  datetime: true,
  endDatetime: true,
  createdAt: true,
  datetimeZ: true,
  plantingDate: true,
  rsStoppedAt: true,
  startDate: true,
  endDate: true,
  archivedAt: true,
  maturityDate: true,
  harvestingDate: true,
  maturityDateComputedAt: true,
  harvestComputedAt: true,
  yieldComputedAt: true,
  ccsComputedAt: true,
  latestSoilSampleAt: true
};
export const DateTimeType = types.custom<string, DateTime>({
  name: 'DateTimeType',
  fromSnapshot(snapshot: string): DateTime {
    return DateTime.fromISO(snapshot);
  },
  toSnapshot(value: DateTime): string {
    return value.toISO();
  },
  isTargetType(value): boolean {
    return DateTime.isDateTime(value);
  },
  getValidationMessage(value): string {
    if (DateTime.isDateTime(value)) {
      return '';
    }
    return `'${value}' is not a valid DateTime object`;
  }
});
const isDigit = (char: string) => char >= '0' && char <= '9';
const isValidISO8601 = (dateStr: string) => {
  // ISO 8601 strict format regex pattern from AgroAPI
  const iso8601Pattern = /^(?:[1-9]\d{3}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1\d|2[0-8])|(?:0[13-9]|1[0-2])-(?:29|30)|(?:0[13578]|1[02])-31)|(?:[1-9]\d(?:0[48]|[2468][048]|[13579][26])|(?:[2468][048]|[13579][26])00)-02-29)T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d?.?(\d+)(?:Z|[+-][01]\d:[0-5]\d)$/;
  return iso8601Pattern.test(dateStr) && DateTime.fromISO(dateStr).isValid;
};
// Function to process snapshot values recursively
const processSnapshot = <T extends object,>(snapshot: T): T => {
  const processedSnapshot = {
    ...snapshot
  };
  Object.keys(snapshot).forEach(key => {
    const value = snapshot[key];

    // Process recursively even if the top-level key is not in the whitelist
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recursively process nested objects
      processedSnapshot[key] = processSnapshot(value);
      return;
    }

    // For non-object values check the whitelist
    if (!DATETIME_PROPERTY_WHITELIST[key]) return;
    if (typeof value === 'string' && isDigit(value[0]) && isValidISO8601(value)) {
      processedSnapshot[key] = DateTime.fromISO(value, {
        zone: 'utc'
      }).toUTC();
    } else if (DateTime.isDateTime(value)) {
      processedSnapshot[key] = value.toUTC();
    }
  });
  return processedSnapshot;
};
export const withDateProcessors = <T extends IAnyModelType,>(modelType: T) => types.snapshotProcessor(modelType, {
  preProcessor(snapshot: SnapshotIn<T>): SnapshotIn<T> {
    return processSnapshot(snapshot);
  },
  postProcessor(snapshot: SnapshotIn<T>): SnapshotIn<T> {
    return snapshot;
  }
});